import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';

import accordionTheme from './chakra/components/Accordion';
import avatarTheme from './chakra/components/Avatar';
import breadcrumbTheme from './chakra/components/Breadcrumb';
import buttonTheme from './chakra/components/Button';
import cardTheme from './chakra/components/Card';
import checkboxTheme from './chakra/components/Checkbox';
import containerTheme from './chakra/components/Container';
import dialogTheme from './chakra/components/Dialog';
import dividerTheme from './chakra/components/Divider';
import drawerTheme from './chakra/components/Drawer';
import fieldTheme from './chakra/components/Field';
import headingTheme from './chakra/components/Heading';
import inputTheme from './chakra/components/Input';
import linkTheme from './chakra/components/Link';
import listTheme from './chakra/components/List';
import menuTheme from './chakra/components/Menu';
import numberInputTheme from './chakra/components/NumberInput';
import popoverTheme from './chakra/components/Popover';
import progressTheme from './chakra/components/Progress';
import progressCircleTheme from './chakra/components/ProgressCircle';
import radioGroupTheme from './chakra/components/RadioGroup';
import selectTheme from './chakra/components/Select';
import spinnerTheme from './chakra/components/Spinner';
import switchTheme from './chakra/components/Switch';
import tableTheme from './chakra/components/Table';
import tabsTheme from './chakra/components/Tabs';
import tagTheme from './chakra/components/Tag';
import textareaTheme from './chakra/components/Textarea';
import tooltipTheme from './chakra/components/Tooltip';

/*
 * Source of truth: frontend/src/styles/designSystemV2.css (vendored from the
 * MonetizeNow Design System bundle, iteration 2). Every token here is a
 * port of a --mn-* CSS var. Do not edit values here without updating the CSS
 * in lockstep.
 *
 * Typeface: Inter, loaded via Google Fonts in frontend/index.html. Akkurat
 * Pro (design system's brand typeface) is not shipped — unlicensed, too heavy.
 */

export const interFont = `'Inter', system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;

export const fonts = {
  body: interFont,
  heading: interFont,
};

export const colors = {
  // Legacy brand token names — retuned to design-system values where they diverge.
  // New tokens added for design-system hovers/actives (tIndigo.600, .700).
  tBlue: {
    base: '#252b37',
    hover: '#EAECF9', // --mn-brand-indigo-50 / selected+hover surface tint
    lightShade: '#B4BCEF',
    light: '#6478FF',
    magenta: '#8C32FF',
    vLShadeMagenta: '#F7ECFF',
    lShadeMagenta: '#BAA1E3',
    bSweet: '#635BFF',
    totalBlue: '#6478FF',
    dark: '#05115E',
    100: '#E6E9FF',
    400: '#4659DA',
  },
  tPurple: {
    dark: '#3D3C65',
    base: '#3D3C65', // headings/nav stay warm-dark
    200: '#3D3C65',
    100: '#3D3C65',
    500: '#3D3C65',
    600: '#070723',
    900: '#3D3C65',
    safety: '#8C32FF',
    linkWater: '#EAECF9',
    inputBox: '#F7ECFF',
    lightGrayPurple: '#7B7A85',
  },
  tIndigo: {
    base: '#4659DA',
    600: '#3B4BBF', // NEW — hover
    700: '#2E3BA0', // NEW — active
    200: '#4659DA',
    100: '#4659DA',
    500: '#4659DA',
    900: '#4659DA',
  },
  tRed: {
    base: '#BF4848',
    magenta: '#F909A7',
    hover: '#802A2A',
    cosmos: '#FFD4D4',
    paleRed: '#FFD5D5',
    cinnabar: '#E53E3E',
    50: '#FFEFEF', // NEW — danger-soft tint
  },
  tRoyalBlue: {
    base: '#4659DA',
  },
  tGray: {
    acGray: '#6E6E81',
    darkPurple: '#606070',
    darkGrayPurple: '#606070',
    lightPurple: '#C8C5D4', // disabled/placeholder + checkbox/radio borders
    back: '#E3E8EC', // --mn-border-subtle
    support: '#F6F6F8', // was #F6F6F6, cool-shifted to --mn-neutral-75
    sidebar: '#FCFCFC',
    drawerSidebar: '#FBFAFA',
    sidebarDark: '#FBFAFA',
    lightGray: '#d3d3d3',
    gray89: '#E3E3E3',
    grey96: '#0000000a',
    light: '#C8C8C8',
    light1: '#FAFAFA',
    back1: '#8C8998',
    disabled: 'rgba(246, 246, 246, 0.75)',
    acBlack: '#333333',
    disabledText: '#A1A2A3',
    disabledBack: '#F0F0F0',
    overlay: '#F6F6F659',
    lightBack: '#F1EFEE',
    shareBack: '#F2F1F3',
    shareFooter: '#dcdde09e',
  },
  // NEW — cool-shifted neutrals for body text, headings, canvas, borders.
  // Avoid leaking into tGray.* which has warm undertones referenced elsewhere.
  tNeutral: {
    canvas: '#FCFCFD', // --mn-bg-canvas
    fg: '#121420', // --mn-fg-primary (body/data text, near-black)
    fgStrong: '#252b37', // --mn-fg-strong (headings)
    fgMuted: '#6E6E81', // --mn-fg-muted
    fgSubtle: '#8C8998', // --mn-fg-subtle
    border200: '#D6D6DE', // --mn-border-default mid
  },
  /**
   * Avatar swatches. Soft tinted surfaces, each paired with a foreground of
   * the same hue at >=5.4:1 so initials stay legible at avatar sizes. Eight
   * hues rather than four so a group of people with similar names still comes
   * out varied; see `getAvatarColorsFromName`.
   */
  tAvatar: {
    redBg: '#FECACA',
    redFg: '#991B1B',
    orangeBg: '#FED7AA',
    orangeFg: '#9A3412',
    amberBg: '#FDE68A',
    amberFg: '#92400E',
    greenBg: '#BBF7D0',
    greenFg: '#166534',
    tealBg: '#99F6E4',
    tealFg: '#115E59',
    blueBg: '#BFDBFE',
    blueFg: '#1E40AF',
    violetBg: '#DDD6FE',
    violetFg: '#5B21B6',
    pinkBg: '#FBCFE8',
    pinkFg: '#9D174D',
  },
  // NEW — magenta ramp for ai/MonetizeIQ entry points
  tMagenta: {
    base: '#8C32FF',
    hover: '#7B25E5', // darker shade for hover/active on tMagenta.base surfaces
    50: '#F7ECFF',
    200: '#BAA1E3',
  },
  darkMCyan: '#319795',
  tCyan: {
    dark: '#319795',
  },
  tWhite: {
    base: '#ffffff',
    titanWhite: '#FAFAFF',
    text: '#FBFAFA',
    focus: 'rgba(255, 244, 226, 0.75)',
    300: '#F8F7F7',
    400: '#E4E4E4',
  },
  tBlack: {
    200: '#8D8D8D',
    300: '#7A7A7A',
  },
  tMud: {
    base: '#460C0C',
  },
  tCreamsicle: {
    base: '#FFF4E2',
  },
  tOrange: {
    base: '#E47915',
    input: '#FFF4E2',
    tangerine: '#FC6C22',
    bg: '#FFF4E2BF',
  },
  tGreen: {
    mDarkShade: '#18A800',
    approval: '#24823F',
    approvalHover: '#1d6933ff',
    light: '#EBFFE8',
    bayside: '#50E3C2',
    lime: '#79C878',
  },
  tTurquoise: {
    ocean: '#09C4F9',
    bayside: '#50E3C2',
  },
  tYellow: {
    base: '#F6E24E',
  },
};

// Semantic tokens — mirror the design system's --mn-* intent layer.
// Components and new code should prefer these over raw brand tokens.
// Each entry references a base `colors` token above via v3's `{colors.x.y}`
// string syntax, except `bg.overlay`, which is a one-off rgba mix with no
// corresponding named brand token.
const ref = (path: string) => ({ value: `{colors.${path}}` });

const semanticColorTokens = {
  bg: {
    canvas: ref('tNeutral.canvas'),
    body: ref('tWhite.base'), // --mn-bg-body
    surface: ref('tWhite.base'),
    subtle: ref('tWhite.titanWhite'),
    muted: ref('tGray.support'),
    hover: ref('tGray.support'),
    pressed: ref('tGray.lightBack'),
    selected: ref('tBlue.hover'),
    overlay: { value: 'rgba(24, 36, 60, 0.86)' },
    inverse: ref('tPurple.600'),
  },
  fg: {
    primary: ref('tNeutral.fg'),
    strong: ref('tNeutral.fgStrong'),
    muted: ref('tNeutral.fgMuted'),
    subtle: ref('tNeutral.fgSubtle'),
    disabled: ref('tGray.lightPurple'),
    onAccent: { value: 'white' },
    link: ref('tIndigo.base'),
    linkHover: ref('tIndigo.700'),
  },
  border: {
    subtle: ref('tGray.back'),
    default: ref('tNeutral.border200'),
    strong: ref('tGray.lightPurple'),
    focus: ref('tIndigo.base'),
    accent: ref('tIndigo.base'),
  },
  action: {
    primary: ref('tIndigo.base'),
    primaryHover: ref('tIndigo.600'),
    primaryActive: ref('tIndigo.700'),
    danger: ref('tRed.base'),
    success: ref('tGreen.approval'),
    ai: ref('tMagenta.base'),
  },
  // Lets `colorPalette="tIndigo"` work: v3 recipes fill with
  // `colorPalette.solid` (e.g. Progress's range), which this brand palette
  // lacked, so the lookup fell back to the gray palette (near-black). v2 used
  // `tIndigo.500`, the same value as `tIndigo.base`.
  tIndigo: {
    solid: ref('tIndigo.base'),
  },
};

// 13px-base type scale (design system spec) anchored on `sm`.
// The app overwhelmingly uses `fontSize="sm"` as its default, so `sm` is
// pinned at the design-system body size (13px) rather than `md`. This
// avoids a sweeping fontSize="sm" → fontSize="md" migration across hundreds
// of call sites and prevents compounding rem-shrinkage when sm-sized
// components are nested inside other sm-sized components.
const fontSizes = {
  xxs: '0.625rem', // 10px — micro chrome only
  xs: '0.75rem', // 12px — eyebrows, dense table cells, tag pills
  sm: '0.8125rem', // 13px — DEFAULT body / data text (was 14px)
  md: '0.875rem', // 14px — labels, slightly larger body (was 16px)
  lg: '1rem', // 16px — section headings, prominent body (was 18px)
  xl: '1.125rem', // 18px — page subtitles (was 20px)
  '2xl': '1.25rem', // 20px — small page titles (was 24px)
  '2.5xl': '1.375rem', // 22px — medium page titles (was 24px)
  '3xl': '1.5rem', // 24px — large page titles (was 30px)
  '4xl': '1.875rem', // 30px — display H1 (was 36px)
  '5xl': '2.25rem', // 36px (was 48px)
  '6xl': '3rem', // 48px (was 64px)
  hero: '4rem', // 64px (was 96px)
};

const letterSpacings = {
  tight: '-0.02em', // was -0.04em — design system spec
  normal: '0',
  wide: '0.04em',
  wider: '0.08em',
  widest: '0.24em',
};

const lineHeights = {
  normal: 'normal',
  none: '1',
  shorter: '1.15', // was 1.1
  short: '1.3',
  base: '1.45', // was 1.5
  tall: '1.6',
  taller: '2',
};

const spacing = {
  header: '5rem',
};

// Radii scale — design system spec + modal-specific 10px token.
const radii = {
  none: '0',
  xs: '2px', // tags
  sm: '4px', // buttons, inputs
  md: '6px', // cards, popovers, menus (smaller surfaces)
  lg: '8px', // modals, large popovers
  xl: '10px', // modal/drawer per iteration-2 spec
  modal: '10px', // explicit alias so Modal recipe intent is clear
  '2xl': '12px',
  full: '9999px',
};

// v3's default config ships `xs`–`xl` as *semantic* shadows (light/dark
// pairs) emitted on `:root, .light`, which out-ranks base tokens on
// `:where(html, .chakra-theme)`. Defined as base tokens, these were dead and
// every card/menu/popover/modal/toast fell back to Chakra's shadows. As
// semantic tokens, their plain values replace the built-in pairs in the
// config merge.
const elevationShadows = {
  xs: '0 1px 2px rgba(7, 7, 35, 0.04)',
  sm: '0 1px 3px rgba(7, 7, 35, 0.06), 0 1px 2px rgba(7, 7, 35, 0.04)',
  // menu/popover — iteration-2 menu-popover.html spec
  md: '0 8px 24px rgba(7, 7, 35, 0.12), 0 2px 4px rgba(7, 7, 35, 0.04)',
  // modal/drawer — iteration-2 modal-drawer.html spec
  lg: '0 20px 50px rgba(7, 7, 35, 0.20), 0 4px 12px rgba(7, 7, 35, 0.08)',
  xl: '0 24px 48px rgba(24, 36, 60, 0.18), 0 4px 12px rgba(7, 7, 35, 0.06)',
};

const shadows = {
  pop: '0 4px 25px rgba(180, 188, 239, 0.38)',
  focus: '0 0 0 3px rgba(70, 89, 218, 0.25)',
  // Chakra components that don't have a custom recipe reach for the
  // `outline` shadow token by default (Breadcrumb, CloseButton, Accordion,
  // etc.) — Chakra ships a blue default that doesn't match our indigo
  // focus halo. Aliasing to the same value as `focus` makes the
  // design-system halo apply universally without per-component overrides.
  outline: '0 0 0 3px rgba(70, 89, 218, 0.25)',
  focusDanger: '0 0 0 3px rgba(191, 72, 72, 0.25)',
  focusSuccess: '0 0 0 3px rgba(36, 130, 63, 0.25)',
  focusAi: '0 0 0 3px rgba(140, 50, 255, 0.25)',
  // Same indigo halo at 40% instead of 25%. The standard `focus` halo reads
  // weakly against light off-state control surfaces (the Switch's
  // `tNeutral.border200` track) on white backgrounds; this is the opt-in
  // stronger halo for those, rather than a per-recipe raw rgba override.
  focusStrong: '0 0 0 3px rgba(70, 89, 218, 0.4)',
  // Inset variant — for inputs / form fields inside popovers, drawers, and
  // other containers where an outer halo would clip or escape the surface.
  // 2px inset reads as "this field is focused" without crossing borders.
  focusInset: 'inset 0 0 0 2px rgba(70, 89, 218, 0.55)',
  focusInsetDanger: 'inset 0 0 0 2px rgba(191, 72, 72, 0.55)',
  // Legacy names kept for back-compat; values aligned to new scale.
  card: '0 1px 2px rgba(7, 7, 35, 0.04)',
  popover: '0 8px 24px rgba(7, 7, 35, 0.12), 0 2px 4px rgba(7, 7, 35, 0.04)',
  sideNavbar: '-2px 0px 16px rgba(200, 197, 212, 0.15)',
  t1: '0 1px 2px rgba(7, 7, 35, 0.04)',
  t2: '0 8px 24px rgba(7, 7, 35, 0.12)',
};

const breakpoints = {
  sm: '431px',
  md: '701px',
  lg: '961px',
  xl: '1201px',
  '2xl': '1537px',
};

// v3 tokens/semanticTokens are wrapped `{ value }` leaves, recursively, per
// category (see TokenSchema/TokenDefinition in
// @chakra-ui/react/dist/types/styled-system/types.d.ts). `colors`/`fonts`
// above stay as plain, readable objects (also used directly by the handful
// of call sites that read raw values rather than through Chakra styling
// props — apexcharts, StripeElements, JSON viewers) and get wrapped here
// rather than hand-duplicated, to avoid transcription drift between the two
// shapes.
type DeepTokenValue<T> = T extends string | number
  ? { value: T }
  : { [K in keyof T]: DeepTokenValue<T[K]> };

function toTokens<T extends Record<string, unknown>>(
  input: T,
): DeepTokenValue<T> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(input)) {
    const value = input[key];
    result[key] =
      value !== null && typeof value === 'object'
        ? toTokens(value as Record<string, unknown>)
        : { value };
  }
  return result as DeepTokenValue<T>;
}

const config = defineConfig({
  // Restores v2's cascade. With layers on, recipes sit in `@layer recipes`
  // while style props are unlayered, and unlayered CSS always wins: a
  // call-site `color` would also override the recipe's `_hover` color, and
  // designSystemV2.css's global `a` rules would beat recipe colors on
  // anything rendered as a link. Without layers, recipe styles and style
  // props merge into one class as in v2, so props override resting styles,
  // recipe state styles (`_hover`, `_selected`...) keep precedence through
  // specificity, and recipes beat bare element rules without `!important`.
  disableLayers: true,
  globalCss: {
    // IMPORTANT: never set `html { font-size }` — Chakra's rem-based tokens
    // (e.g. fontSizes.sm = 0.8125rem = 13px) assume browser default 16px root.
    // Body font-size is set here so unstyled text inherits the design-system
    // baseline; Chakra components that opt into a fontSize token resolve
    // against the 16px root regardless.
    html: {
      fontFamily: `${interFont} !important`,
      // v2's color-mode provider set this on <html>; v3 sets nothing, so the
      // page scrollbar followed a dark OS theme and rendered black. The app
      // is light-only.
      colorScheme: 'light',
    },
    body: {
      fontFamily: `${interFont} !important`,
      fontSize: 'sm', // 13px design-system body
      fontFeatureSettings: "'tnum'",
      fontVariantNumeric: 'tabular-nums',
    },
    // v3's preflight gives every element `font: inherit`, so form controls
    // now inherit body's tabular figures and their values read as monospace.
    // Under v2 the browser's own form-control font reset kept them
    // proportional. Element specificity, so recipes and `[data-numeric]`
    // still win where tabular figures are wanted.
    'input, textarea, select, button': {
      fontVariantNumeric: 'normal',
      fontFeatureSettings: 'normal',
    },
    // v3's defaults add a gray `*::selection` that, injected after
    // designSystemV2.css, beats its brand-indigo `::selection` at equal
    // specificity. v2 shipped no selection style, so restate the design
    // system's own here.
    '*::selection': {
      bg: 'var(--mn-brand-indigo-100)',
      color: 'var(--mn-fg-strong)',
    },
    // v2's reset hid the native spin buttons on number inputs; v3's preflight
    // leaves them showing on hover.
    "input[type='number']::-webkit-inner-spin-button, input[type='number']::-webkit-outer-spin-button":
      {
        WebkitAppearance: 'none',
        margin: 0,
      },
    // Suppress the default browser outline; components opt back into a
    // visible focus ring via `_focusVisible: { boxShadow: 'focus' }` (see
    // ./chakra/focusRing.ts).  Do NOT suppress focus-visible globally —
    // that breaks keyboard nav.
    '*:focus': {
      outline: 'none',
    },
    // TinyMCE renders its menus and dialogs into `.tox-tinymce-aux` on <body>,
    // outside any modal. v3's modal dialog sets `pointer-events: none` on the
    // body while open (v2's didn't), which left those menus unclickable.
    '.tox-tinymce-aux': {
      pointerEvents: 'auto',
    },
  },
  theme: {
    breakpoints,
    tokens: {
      colors: toTokens(colors),
      fonts: toTokens(fonts),
      fontSizes: toTokens(fontSizes),
      letterSpacings: toTokens(letterSpacings),
      lineHeights: toTokens(lineHeights),
      spacing: toTokens(spacing),
      radii: toTokens(radii),
      shadows: toTokens(shadows),
    },
    semanticTokens: {
      colors: semanticColorTokens,
      shadows: toTokens(elevationShadows),
    },
    recipes: {
      button: buttonTheme,
      container: containerTheme,
      heading: headingTheme,
      input: inputTheme,
      link: linkTheme,
      separator: dividerTheme,
      spinner: spinnerTheme,
      textarea: textareaTheme,
    },
    slotRecipes: {
      accordion: accordionTheme,
      avatar: avatarTheme,
      breadcrumb: breadcrumbTheme,
      card: cardTheme,
      checkbox: checkboxTheme,
      dialog: dialogTheme,
      drawer: drawerTheme,
      field: fieldTheme,
      list: listTheme,
      menu: menuTheme,
      nativeSelect: selectTheme,
      numberInput: numberInputTheme,
      popover: popoverTheme,
      progress: progressTheme,
      progressCircle: progressCircleTheme,
      radioGroup: radioGroupTheme,
      switch: switchTheme,
      table: tableTheme,
      tabs: tabsTheme,
      tag: tagTheme,
      tooltip: tooltipTheme,
    },
  },
});

const system = createSystem(defaultConfig, config);

export default system;
